
import Vue from 'vue';
import _chunk from 'lodash/chunk';
import { mapActions, mapState } from 'vuex';
import { db, functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import BaseButton from '@/components/base/BaseButton.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import {
	ALPHA_BATCH,
	ALPHA_HOLDS,
	ALPHA_MAX_EMAILS,
	ALPHA_MAX_FILE_BYTES,
	AlphaHold,
	AlphaHoldDoc,
	AlphaMembership,
	AlphaReviewRow,
	AlphaRowGroup,
	alphaRowGroup,
	AlphaRowStatus,
	BoardsV2AlphaMatch,
	CsvEmails,
	groupMemberships,
	heldByLabel,
	holdData,
	holdEmails,
	holdFromDoc,
	holdSummary,
	leftOutNote,
	newestFirst,
	readCsvEmails,
	rowFromMatch,
} from './alphaCsv';

type Filter = 'all' | AlphaRowGroup;
type Counts = Record<AlphaRowGroup, number> & { all: number };
// The callables' answers; `message` is shown as is
interface LookupResult { success: boolean, message?: string, matches?: BoardsV2AlphaMatch[] }
interface SetResult { success: boolean, message?: string, changed?: number }

const STATUS_LABEL: Record<AlphaRowStatus, string> = {
	'checking': 'Checking',
	'error': 'Not checked',
	'ready': 'In the system',
	'in-alpha': 'Already in',
	'admin': 'Admin',
	'added': 'Added',
	'missing': 'Not found',
	'deleted': 'Deleted',
	'invalid': 'Invalid',
};
const STATUS_ICON: Record<AlphaRowStatus, string> = {
	'checking': '',
	'error': 'mdi-help',
	'ready': 'mdi-check',
	'in-alpha': 'mdi-check-all',
	'admin': 'mdi-shield-account-outline',
	'added': 'mdi-check-all',
	'missing': 'mdi-account-question-outline',
	'deleted': 'mdi-account-cancel-outline',
	'invalid': 'mdi-email-alert-outline',
};

async function call<T> (name: string, data: unknown): Promise<T> {
	const result = await functions.httpsCallable(name)(data);
	return result.data as T;
}

/**
 * The Boards v2 alpha's CSV upload, on the Boards v2 Rollout page (IL-107).
 * Reads every email in a file, checks each against RunDiffusion's accounts
 * (lookupBoardsV2AlphaEmails), and shows a row per email: in the system (with
 * its teams and companies), already in, or not in the system. The admin
 * removes rows, one at a time or every one not in the system at once, then
 * adds the accounts left (setBoardsV2Alpha). Rows not in the system are never
 * sent.
 *
 * Or holds the list to add later (IL-114): "Hold for later" keeps its rows'
 * emails in boardsV2AlphaHolds and adds no one. Held lists show above the drop
 * zone, live; "Review and add" opens one here, checking every email against
 * the accounts afresh, and adding them deletes it. Held again, it is saved over.
 */
export default Vue.extend({
	name: 'AlphaCsvReview',
	data () {
		return {
			// lists held to add later, the last held first
			holds: [] as AlphaHold[],
			// the held list this review was opened from; null for a file
			hold: null as AlphaHold | null,
			holding: false,
			// adding them deleted the held list they came from
			holdCleared: false,
			discard: { open: false, id: '', name: '', count: 0 },
			discarding: false,
			unsubscribe: null as (() => void) | null,
			rows: [] as AlphaReviewRow[],
			fileName: '',
			// emails in the file, as read (rows removed since don't change it)
			fileCount: 0,
			repeats: 0,
			filter: 'all' as Filter,
			dragOver: false,
			reading: false,
			checking: false,
			checkError: '',
			// a check still answering for a list since replaced is dropped
			runId: 0,
			adding: false,
			done: false,
			addedCount: 0,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		closeLabel (): string {
			if (this.done) return 'Done';
			// a held list stays held
			return this.hold ? 'Close' : 'Start over';
		},
		discardBody (): string {
			const n: number = this.discard.count;
			return `Its ${n} ${n === 1 ? 'email is' : 'emails are'} no longer held to add later. No one is added to the alpha, or taken out of it.`;
		},
		counts (): Counts {
			const counts: Counts = { all: this.rows.length, 'ready': 0, 'has-it': 0, 'not-found': 0, 'pending': 0 };
			this.rows.forEach((row: AlphaReviewRow) => {
				counts[alphaRowGroup(row.status)]++;
			});
			return counts;
		},
		checkedCount (): number {
			return this.rows.filter((row: AlphaReviewRow) => row.status !== 'checking').length;
		},
		checkProgress (): number {
			return this.rows.length ? (100 * this.checkedCount) / this.rows.length : 0;
		},
		filters (): Array<{ key: Filter, label: string, count: number }> {
			const filters: Array<{ key: Filter, label: string, count: number }> = [
				{ key: 'all', label: 'All', count: this.counts.all },
				{ key: 'ready', label: 'In the system', count: this.counts.ready },
				{ key: 'has-it', label: 'Already in', count: this.counts['has-it'] },
				{ key: 'not-found', label: 'Not in the system', count: this.counts['not-found'] },
			];
			if (this.counts.pending && !this.checking) filters.push({ key: 'pending', label: 'Not checked', count: this.counts.pending });
			return filters;
		},
		visibleRows (): AlphaReviewRow[] {
			if (this.filter === 'all') return this.rows;
			return this.rows.filter((row: AlphaReviewRow) => alphaRowGroup(row.status) === this.filter);
		},
		fileDetail (): string {
			const emails: string = `${this.fileCount} ${this.fileCount === 1 ? 'email' : 'emails'}`;
			if (this.hold) return `${emails} · ${this.heldByLabel(this.hold)}`;
			const parts: string[] = [`${emails} in the file`];
			if (this.repeats) parts.push(`${this.repeats} ${this.repeats === 1 ? 'repeat' : 'repeats'} merged`);
			return parts.join(' · ');
		},
		footNote (): string {
			if (this.done) {
				const people: string = `${this.addedCount} ${this.addedCount === 1 ? 'person' : 'people'}`;
				const cleared: string = this.holdCleared ? ' The held list is cleared.' : '';
				return `Added ${people}. They have Boards v2 now, on pages already open too.${cleared}`;
			}
			if (this.checking) return 'Adding opens once every email is checked.';
			return leftOutNote([
				{ count: this.counts['not-found'], label: 'not in the system' },
				{ count: this.counts['has-it'], label: 'already in' },
				{ count: this.counts.pending, label: 'not checked' },
			]) || 'Every email here is an account, ready to add.';
		},
	},
	watch: {
		// a filter emptied by removing its last row goes back to all
		counts (counts: Counts) {
			if (this.filter !== 'all' && !counts[this.filter]) this.filter = 'all';
		},
	},
	mounted () {
		this.unsubscribe = db.collection(ALPHA_HOLDS).onSnapshot((snap) => {
			this.holds = snap.docs
				.map((doc) => holdFromDoc(doc.id, doc.data({ serverTimestamps: 'estimate' }) as AlphaHoldDoc))
				.sort(newestFirst);
		}, (e: Error) => {
			console.error('[boards v2 alpha] the held lists failed to load', e);
		});
	},
	beforeDestroy () {
		if (this.unsubscribe) this.unsubscribe();
		this.unsubscribe = null;
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		holdSummary (hold: AlphaHold): string {
			return holdSummary(hold);
		},
		heldByLabel (hold: AlphaHold): string {
			return heldByLabel(hold, new Date());
		},
		groupOf (row: AlphaReviewRow): AlphaRowGroup {
			return alphaRowGroup(row.status);
		},
		statusLabel (status: AlphaRowStatus): string {
			return STATUS_LABEL[status];
		},
		statusIcon (status: AlphaRowStatus): string {
			return STATUS_ICON[status];
		},
		statusDetail (row: AlphaReviewRow): string {
			switch (row.status) {
			case 'checking': return 'Checking...';
			case 'error': return 'Couldn\'t be checked';
			case 'admin': return 'RunDiffusion admin, who has Boards v2 already';
			case 'missing': return 'No RunDiffusion account has this email';
			case 'deleted': return 'This account was deleted';
			case 'invalid': return 'Not an email address';
			default: return 'Personal account, on no team';
			}
		},
		memberships (row: AlphaReviewRow): AlphaMembership[] {
			return groupMemberships(row.teams);
		},
		pickFile () {
			if (this.reading) return;
			const input = this.$refs.file as HTMLInputElement;
			input.value = '';
			input.click();
		},
		onFilePicked (e: Event) {
			const file: File | undefined = ((e.target as HTMLInputElement).files || [])[0];
			if (file) this.load(file);
		},
		onDrop (e: DragEvent) {
			this.dragOver = false;
			const file: File | undefined = (e.dataTransfer && e.dataTransfer.files[0]) || undefined;
			if (file) this.load(file);
		},
		fail (message: string) {
			this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message, show: true });
		},
		async load (file: File) {
			if (file.size > ALPHA_MAX_FILE_BYTES) {
				this.fail(`${file.name} is too large. Choose a CSV under 5 MB.`);
				return;
			}
			this.reading = true;
			let text: string = '';
			try {
				text = await file.text();
			} catch (e) {
				console.error('[boards v2 alpha] the file could not be read', e);
				this.fail(`${file.name} couldn't be read`);
				return;
			} finally {
				this.reading = false;
			}
			const read: CsvEmails = readCsvEmails(text);
			const count: number = read.emails.length + read.invalid.length;
			if (!count) {
				this.fail(`No emails in ${file.name}`);
				return;
			}
			if (count > ALPHA_MAX_EMAILS) {
				this.fail(`${file.name} has ${count} emails. Add at most ${ALPHA_MAX_EMAILS} at a time.`);
				return;
			}
			this.start(file.name, read, null);
		},
		// Reviews a list: a file's, or a held list's (checked against the accounts afresh)
		start (name: string, read: CsvEmails, hold: AlphaHold | null) {
			this.fileName = name;
			this.fileCount = read.emails.length + read.invalid.length;
			this.repeats = read.repeats;
			this.hold = hold;
			this.holdCleared = false;
			this.filter = 'all';
			this.done = false;
			this.addedCount = 0;
			this.rows = [
				...read.emails.map((email: string): AlphaReviewRow => ({ email, status: 'checking', userId: null, teams: [] })),
				...read.invalid.map((email: string): AlphaReviewRow => ({ email, status: 'invalid', userId: null, teams: [] })),
			];
			this.check(read.emails);
		},
		openHold (hold: AlphaHold) {
			this.start(hold.fileName, holdEmails(hold), hold);
		},
		// Keeps the list as it stands, to add later, and closes the review. No one is added.
		async holdForLater () {
			if (!this.rows.length || !this.user) return;
			this.holding = true;
			const holds = db.collection(ALPHA_HOLDS);
			// a held list opened here is saved over
			const ref = this.hold ? holds.doc(this.hold.id) : holds.doc();
			const n: number = this.rows.length;
			try {
				await ref.set(holdData(this.rows, this.fileName, { id: this.user.id, email: this.user.email || '' }));
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Held ${n} ${n === 1 ? 'email' : 'emails'} for later. No one was added.`,
					show: true,
				});
				this.reset();
			} catch (e) {
				console.error('[boards v2 alpha] holding the list failed', e);
				this.fail('Couldn\'t hold the list. Try again.');
			} finally {
				this.holding = false;
			}
		},
		askDiscard (hold: AlphaHold) {
			this.discard = { open: true, id: hold.id, name: hold.fileName, count: hold.emails.length };
		},
		onDiscardInput (open: boolean) {
			this.discard = { ...this.discard, open };
		},
		async onConfirmDiscard (close: () => void) {
			const { id, name } = this.discard;
			this.discarding = true;
			try {
				await db.collection(ALPHA_HOLDS).doc(id).delete();
				this.updateSnackbar({
					status: SNACKBAR_STATUS.INFO,
					message: `Discarded ${name}`,
					show: true,
				});
			} catch (e) {
				console.error('[boards v2 alpha] discarding a held list failed', id, e);
				this.fail(`Couldn't discard ${name}. Try again.`);
			} finally {
				this.discarding = false;
			}
			close();
		},
		// Its people are in, so the held list they came from is done with
		async clearHold (hold: AlphaHold) {
			try {
				await db.collection(ALPHA_HOLDS).doc(hold.id).delete();
				this.holdCleared = true;
			} catch (e) {
				console.error('[boards v2 alpha] clearing a held list failed', hold.id, e);
				this.fail(`They're in, but ${hold.fileName} is still held. Discard it from Held for later.`);
			}
		},
		// Checks emails a batch at a time; a batch that fails is left to check again
		async check (emails: string[]) {
			const run: number = ++this.runId;
			this.checking = true;
			this.checkError = '';
			for (const part of _chunk(emails, ALPHA_BATCH)) {
				let matches: Record<string, BoardsV2AlphaMatch> | null = null;
				try {
					const result: LookupResult = await call<LookupResult>('lookupBoardsV2AlphaEmails', { emails: part });
					if (!result.success || !result.matches) throw new Error(result.message);
					matches = result.matches.reduce((acc: Record<string, BoardsV2AlphaMatch>, m: BoardsV2AlphaMatch) => ({ ...acc, [m.email]: m }), {});
				} catch (e) {
					console.error('[boards v2 alpha] a lookup failed', e);
					this.checkError = (e as Error).message || '';
				}
				if (run !== this.runId) return;
				const inPart: Set<string> = new Set(part);
				const found: Record<string, BoardsV2AlphaMatch> | null = matches;
				this.rows = this.rows.map((row: AlphaReviewRow): AlphaReviewRow => {
					if (!inPart.has(row.email)) return row;
					if (found && found[row.email]) return rowFromMatch(found[row.email]);
					return { ...row, status: 'error' };
				});
			}
			this.checking = false;
		},
		checkAgain () {
			const emails: string[] = this.rows.filter((row: AlphaReviewRow) => row.status === 'error').map((row: AlphaReviewRow) => row.email);
			this.rows = this.rows.map((row: AlphaReviewRow): AlphaReviewRow => (row.status === 'error' ? { ...row, status: 'checking' } : row));
			this.check(emails);
		},
		remove (row: AlphaReviewRow) {
			this.rows = this.rows.filter((r: AlphaReviewRow) => r.email !== row.email);
			if (!this.rows.length) this.reset();
		},
		removeNotFound () {
			const n: number = this.counts['not-found'];
			this.rows = this.rows.filter((row: AlphaReviewRow) => alphaRowGroup(row.status) !== 'not-found');
			this.updateSnackbar({
				status: SNACKBAR_STATUS.INFO,
				message: `Removed ${n} not in the system`,
				show: true,
			});
			if (!this.rows.length) this.reset();
		},
		reset () {
			this.runId++;
			this.rows = [];
			this.fileName = '';
			this.fileCount = 0;
			this.repeats = 0;
			this.hold = null;
			this.holdCleared = false;
			this.filter = 'all';
			this.checking = false;
			this.checkError = '';
			this.done = false;
			this.addedCount = 0;
		},
		async add () {
			const ready: AlphaReviewRow[] = this.rows.filter((row: AlphaReviewRow) => row.status === 'ready');
			if (!ready.length) return;
			this.adding = true;
			let added: number = 0;
			try {
				for (const part of _chunk(ready, ALPHA_BATCH)) {
					const result: SetResult = await call<SetResult>('setBoardsV2Alpha', {
						userIds: part.map((row: AlphaReviewRow) => row.userId),
						on: true,
					});
					if (!result.success) throw new Error(result.message);
					const done: Set<string> = new Set(part.map((row: AlphaReviewRow) => row.email));
					this.rows = this.rows.map((row: AlphaReviewRow): AlphaReviewRow => (done.has(row.email) ? { ...row, status: 'added' } : row));
					added += part.length;
				}
				this.addedCount = added;
				this.done = true;
				this.filter = 'all';
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Added ${added} ${added === 1 ? 'person' : 'people'} to the Boards v2 alpha`,
					show: true,
				});
				if (this.hold) await this.clearHold(this.hold);
			} catch (e) {
				console.error('[boards v2 alpha] adding failed', e);
				this.fail(added
					? `Added ${added}, then stopped: ${(e as Error).message || 'try again'}`
					: `Couldn't add them: ${(e as Error).message || 'try again'}`);
			} finally {
				this.adding = false;
			}
		},
	},
	components: {
		BaseButton,
		ConfirmDialog,
	},
});
